import type { HubSpotOutboundCallEvent } from '@/types/hubspot';
import type { Json } from '@vonage/client-sdk';
import { type ReactNode, createContext, useCallback, useMemo, useRef } from 'react';

import { logger } from '@/services/logger/logger';
import { useVonageSessionError } from '../use-vonage-session-error';
import { cleanupVonageCallState } from '../vonage-call-cleanup';
import { useVonageClient } from '../vonage-client-provider';
import { useDtmf } from './hooks/use-dtmf';
import { useEarmuff } from './hooks/use-earmuff';
import { useHangup } from './hooks/use-hangup';
import { useIncomingCall } from './hooks/use-incoming-call';
import { useMute } from './hooks/use-mute';
import { type SayOptions, useSay } from './hooks/use-say';
import { useServerCall } from './hooks/use-server-call';
import {
  type VoiceCallContext,
  type VoiceCallStateStatus,
  type VoiceContact,
  type VoiceContactExtra,
  type VoiceContactUserLine,
  useVoiceCallState,
} from './voice-call-container';

interface VoiceCallActionsContextType {
  // Actions
  startCall: (
    callCtx: VoiceCallContext,
    contact: VoiceContact,
    context?: Json,
    hubspotCallEvent?: HubSpotOutboundCallEvent,
    options?: { signal?: AbortSignal },
  ) => Promise<boolean>;
  answerCall: () => Promise<boolean>;
  rejectCall: () => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => Promise<void>;
  toggleEarmuff: () => Promise<void>;
  sendDtmf: (value: string) => Promise<void>;
  say: (text: string, options?: SayOptions) => Promise<void>;
  updateContact: (
    contact?: VoiceContact,
    extra?: VoiceContactExtra,
    userLine?: VoiceContactUserLine,
  ) => void;
  transferCall: () => void;
  transferCallFailed: () => void;
  setSessionErrorRecoveryHandler: (handler: SessionErrorRecoveryHandler | null) => void;

  // State (read-only)
  isRinging: boolean;
  isMuted: boolean;
  isEarmuffed: boolean;
  direction: 'incoming' | 'outgoing' | 'unknown';
  contact?: VoiceContact;
  callContext?: VoiceCallContext;
  extra?: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
  callStatus: VoiceCallStateStatus;
  invite: {
    from?: string;
    channel?: string;
  };
  callId?: string;
  from?: {
    name: string;
    channel: string;
  };
}

type SessionErrorRecoveryHandler = () => void | Promise<void>;

/**
 * @internal
 * Use `useVoiceCallActions` instead to access this context.
 */
export const VoiceCallActionsContext = createContext<VoiceCallActionsContextType | null>(null);

interface VoiceCallActionsProviderProps {
  children: ReactNode;
}

/**
 * Provider that instantiates all voice hooks once and exposes their APIs through context.
 * This eliminates duplicate event listeners and prevents race conditions.
 *
 * @remarks
 * Must be nested inside VoiceCallContainer and VonageClientProvider.
 */
export const VoiceCallActionsProvider = ({ children }: VoiceCallActionsProviderProps) => {
  // Instantiate all hooks once
  const serverCallHook = useServerCall();
  const incomingCallHook = useIncomingCall();
  const muteHook = useMute();
  const earmuffHook = useEarmuff();
  const hangupHook = useHangup();
  const dtmfHook = useDtmf();
  const sayHook = useSay();
  const [callState, callDispatch] = useVoiceCallState();
  const vonageClient = useVonageClient();
  const sessionErrorRecoveryHandlerRef = useRef<SessionErrorRecoveryHandler | null>(null);

  const setSessionErrorRecoveryHandler = useCallback(
    (handler: SessionErrorRecoveryHandler | null) => {
      sessionErrorRecoveryHandlerRef.current = handler;
    },
    [],
  );

  // Register session error handler to clean up call state
  useVonageSessionError(async () => {
    const currentCallId = callState.callId;
    const currentCallState = callState.callState;
    const currentDirection = callState.direction;

    logger.info('[VoiceCallActionsProvider] Session error detected, cleaning up call state', {
      callId: currentCallId,
      callState: currentCallState,
      direction: currentDirection,
    });

    const sessionErrorRecoveryHandler = sessionErrorRecoveryHandlerRef.current;
    if (sessionErrorRecoveryHandler) {
      logger.info(
        '[VoiceCallActionsProvider] Delegating session error cleanup to recovery handler',
      );

      try {
        await sessionErrorRecoveryHandler();
        return;
      } catch (error) {
        logger.error(
          '[VoiceCallActionsProvider] Session error recovery handler failed, falling back to local cleanup',
          {
            error: error instanceof Error ? error.message : String(error),
          },
        );
      }
    }

    // Attempt to cleanup Vonage SDK state (will log if call already gone)
    if (currentCallId) {
      await cleanupVonageCallState(vonageClient, {
        attemptHangup: false, // Don't attempt hangup - session is already dead
        reason: 'session_error',
        callId: currentCallId,
        context: {
          previousCallState: currentCallState,
        },
      });
    }

    // Clear local React state if there's an active call
    if (currentCallState !== 'idle') {
      logger.info('[VoiceCallActionsProvider] Clearing local call state due to session error');
      callDispatch({
        type: 'call:hangup',
        reason: 'session_error' as any, // Not in the HangupReason enum but accepted by reducer
      });
    }
  });

  // Operation guards to prevent concurrent calls
  const answerInProgress = useRef(false);
  const rejectInProgress = useRef(false);
  const hangupInProgress = useRef(false);
  const startCallInProgress = useRef(false);

  // Sequential queues for mute/earmuff to prevent stale state reads
  const muteQueue = useRef<Promise<void>>(Promise.resolve());
  const earmuffQueue = useRef<Promise<void>>(Promise.resolve());

  // Guarded startCall
  const startCall = useCallback(
    async (
      callCtx: VoiceCallContext,
      contact: VoiceContact,
      context?: Json,
      hubspotCallEvent?: HubSpotOutboundCallEvent,
      options?: { signal?: AbortSignal },
    ) => {
      if (startCallInProgress.current) {
        logger.warn('[VoiceCallActions] startCall in progress, ignoring duplicate');
        return true;
      }
      startCallInProgress.current = true;
      try {
        return await serverCallHook.startCall(callCtx, contact, context, hubspotCallEvent, options);
      } finally {
        startCallInProgress.current = false;
      }
    },
    [serverCallHook],
  );

  // Guarded answerCall
  const answerCall = useCallback(async (): Promise<boolean> => {
    if (answerInProgress.current) {
      logger.warn('[VoiceCallActions] answerCall in progress, ignoring duplicate');
      return false;
    }
    answerInProgress.current = true;
    try {
      return await incomingCallHook.answerCall();
    } finally {
      answerInProgress.current = false;
    }
  }, [incomingCallHook]);

  // Guarded rejectCall
  const rejectCall = useCallback(async () => {
    if (rejectInProgress.current) {
      logger.warn('[VoiceCallActions] rejectCall in progress, ignoring duplicate');
      return;
    }
    rejectInProgress.current = true;
    try {
      await incomingCallHook.rejectCall();
    } finally {
      rejectInProgress.current = false;
    }
  }, [incomingCallHook]);

  // Guarded hangup
  const hangup = useCallback(async () => {
    if (hangupInProgress.current) {
      logger.warn('[VoiceCallActions] hangup in progress, ignoring duplicate');
      return;
    }
    hangupInProgress.current = true;
    try {
      await hangupHook.hangup();
    } finally {
      hangupInProgress.current = false;
    }
  }, [hangupHook]);

  // Sequential toggleMute to prevent stale state reads
  const toggleMute = useCallback(async () => {
    muteQueue.current = muteQueue.current.then(() => muteHook.toggleMute());
    await muteQueue.current;
  }, [muteHook]);

  // Sequential toggleEarmuff to prevent stale state reads
  const toggleEarmuff = useCallback(async () => {
    earmuffQueue.current = earmuffQueue.current.then(() => earmuffHook.toggleEarmuff());
    await earmuffQueue.current;
  }, [earmuffHook]);

  // Direct pass-through for sendDtmf (no race condition risk)
  const sendDtmf = useCallback(
    async (value: string) => {
      await dtmfHook.sendDtmf(value);
    },
    [dtmfHook],
  );

  // Direct pass-through for say (no race condition risk)
  const say = useCallback(
    async (text: string, options?: SayOptions) => {
      await sayHook.say(text, options);
    },
    [sayHook],
  );

  const updateContact = useCallback(
    (contact?: VoiceContact, extra?: VoiceContactExtra, userLine?: VoiceContactUserLine) => {
      if (contact) {
        callDispatch({ type: 'call:contact:updated', contact, extra, userLine });
      } else {
        callDispatch({ type: 'call:contact:reset' });
      }
    },
    [callDispatch],
  );

  const transferCall = useCallback(() => {
    callDispatch({ type: 'call:transferring' });
  }, [callDispatch]);

  const transferCallFailed = useCallback(() => {
    callDispatch({ type: 'call:transfer:failed' });
  }, [callDispatch]);

  const isRinging = serverCallHook.isRinging || incomingCallHook.isRinging;

  const contextValue = useMemo<VoiceCallActionsContextType>(
    () => ({
      // Actions
      startCall,
      answerCall,
      rejectCall,
      hangup,
      toggleMute,
      toggleEarmuff,
      sendDtmf,
      say,
      updateContact,
      transferCall,
      transferCallFailed,
      setSessionErrorRecoveryHandler,

      // State
      isRinging,
      isMuted: muteHook.isMuted,
      isEarmuffed: earmuffHook.isEarmuffed,
      direction: callState.direction,
      contact: callState.contact,
      extra: callState.extra,
      userLine: callState.userLine,
      callStatus: callState.callState,
      callContext: callState.callCtx,
      from: callState.from,
      callId: callState.callId,
      invite: incomingCallHook.invite,
    }),
    [
      startCall,
      answerCall,
      rejectCall,
      hangup,
      toggleMute,
      toggleEarmuff,
      sendDtmf,
      say,
      updateContact,
      transferCall,
      transferCallFailed,
      setSessionErrorRecoveryHandler,
      isRinging,
      muteHook.isMuted,
      earmuffHook.isEarmuffed,
      callState.direction,
      callState.contact,
      callState.callCtx,
      callState.extra,
      callState.userLine,
      callState.callState,
      callState.from,
      callState.callId,
      incomingCallHook.invite,
    ],
  );

  return (
    <VoiceCallActionsContext.Provider value={contextValue}>
      {children}
    </VoiceCallActionsContext.Provider>
  );
};
